import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import currency from "currency.js";
import bkstApi from "../../api";
import { OFFLINE_PAYMENT_METHOD_LABELS } from "../../common/util";
import TouchAmountEntry from "../../order/component/TouchAmountEntry";

export function isOfflineTransaction(tx, paymentMethods) {
  if (!tx?.orderId || !tx?.id) {
    return false;
  }
  const pmCode = String(tx?.pmCode || "").toLowerCase();
  if (OFFLINE_PAYMENT_METHOD_LABELS[pmCode]) {
    return true;
  }
  return Boolean(paymentMethods?.[pmCode]);
}

export function EditOfflineTransactionDialog({ tx, onClose, onUpdated }) {
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const close = () => {
    if (!saving) {
      onClose();
    }
  };

  const save = () => {
    const nextAmount = currency(amount).value;
    if (!(nextAmount > 0)) {
      setError("Amount must be greater than zero");
      return;
    }

    setSaving(true);
    setError("");
    bkstApi
      .put(`/my/order/${tx.orderId}/transaction/${tx.id}`, { amount: nextAmount })
      .then((res) => {
        const next = { ...tx, amount: res.data.amount };
        delete next.tendered;
        onUpdated(next);
        setSaving(false);
        onClose();
      })
      .catch((err) => {
        setSaving(false);
        setError(err.response?.data?.message || "Couldn't update this transaction");
      });
  };

  return (
    <Dialog open={Boolean(tx)} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{currency(tx?.amount || 0).format()} | Edit Amount</DialogTitle>
      {tx && (
        <>
          <IconButton
            onClick={close}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TouchAmountEntry
              hideFees
              payment={{ amount, includeFees: false, total: amount }}
              onChange={(val) => setAmount(val.amount)}
            />
            <Box mt={2} sx={{ display: "flex", gap: 1 }}>
              <Button
                fullWidth
                color="secondary"
                variant="outlined"
                onClick={close}
                disabled={saving}
                sx={{ flex: 1, fontSize: "1.25rem", py: 1.5 }}
              >
                CANCEL
              </Button>
              <Button
                fullWidth
                color="primary"
                variant="contained"
                onClick={save}
                disabled={saving}
                sx={{ flex: 1, fontSize: "1.25rem", py: 1.5 }}
              >
                {saving ? "SAVING" : "SAVE"}
              </Button>
            </Box>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

export default function OfflineTransactionMenu({ tx, onEdit }) {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <IconButton
        size="small"
        title="More options"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          sx={{ padding: "20px" }}
          onClick={() => {
            setAnchorEl(null);
            onEdit(tx);
          }}
        >
          Edit amount
        </MenuItem>
      </Menu>
    </>
  );
}
